import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Wallet, Calendar, Plus, Filter, Download } from 'lucide-react';
import TransactionForm from './TransactionForm';
import BudgetForm from './BudgetForm';

const Finances = ({ apiKey }) => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      
      // Fetch summary
      const summaryRes = await fetch(`${apiUrl}/api/finance/summary/`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      
      if (summaryRes.status === 401) {
        console.error('❌ Clé API invalide - reconnexion requise');
        alert('Clé API invalide. Veuillez vous déconnecter et reconnecter.');
        setLoading(false);
        return;
      }
      
      if (!summaryRes.ok) {
        throw new Error(`HTTP error! status: ${summaryRes.status}`);
      }
      
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch recent transactions
      const transRes = await fetch(`${apiUrl}/api/finance/transactions/?limit=50`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (transRes.ok) {
        const transData = await transRes.json();
        setTransactions(transData);
      }

      // Fetch current month budgets
      const now = new Date();
      const budgetRes = await fetch(
        `${apiUrl}/api/finance/budgets/?month=${now.getMonth() + 1}&year=${now.getFullYear()}`,
        { headers: { 'Authorization': `Bearer ${apiKey}` } }
      );
      if (budgetRes.ok) {
        const budgetData = await budgetRes.json();
        setBudgets(budgetData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setLoading(false);
    }
  };

  const handleTransactionSave = () => {
    setShowTransactionForm(false);
    setSelectedTransaction(null);
    fetchFinancialData();
  };

  const handleBudgetSave = () => {
    setShowBudgetForm(false);
    setSelectedBudget(null);
    fetchFinancialData();
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction ?')) return;
    
    try {
      await fetch(`${apiUrl}/api/finance/transactions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      fetchFinancialData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des données financières...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Finances</h2>
          <p className="text-gray-600">Gestion financière de GOS MÉDIA</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTransactionForm(true)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nouvelle Transaction
          </button>
          <button
            onClick={() => setShowBudgetForm(true)}
            className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nouveau Budget
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Tableau de Bord
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'transactions'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('budgets')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'budgets'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Budgets
          </button>
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && summary && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Revenue */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="h-8 w-8 opacity-80" />
                <span className="text-green-100 text-sm">Revenus</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.total_revenue.toLocaleString()}</div>
              <div className="text-green-100 text-sm">Total revenus</div>
            </div>

            {/* Total Expenses */}
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <TrendingDown className="h-8 w-8 opacity-80" />
                <span className="text-red-100 text-sm">Dépenses</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.total_expenses.toLocaleString()}</div>
              <div className="text-red-100 text-sm">Total dépenses</div>
            </div>

            {/* Net Profit */}
            <div className={`bg-gradient-to-br ${
              summary.net_profit >= 0 ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'
            } rounded-xl p-6 text-white`}>
              <div className="flex items-center justify-between mb-4">
                <Wallet className="h-8 w-8 opacity-80" />
                <span className="text-blue-100 text-sm">Profit</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.net_profit.toLocaleString()}</div>
              <div className="text-blue-100 text-sm">
                {summary.net_profit >= 0 ? 'Bénéfice net' : 'Perte nette'}
              </div>
            </div>
          </div>

          {/* Revenue by Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Revenus par Catégorie
              </h3>
              <div className="space-y-3">
                {Object.entries(summary.revenue_by_category).length > 0 ? (
                  Object.entries(summary.revenue_by_category).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-gray-700">{category}</span>
                      <span className="font-semibold text-green-600">${amount.toLocaleString()}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucun revenu enregistré</p>
                )}
              </div>
            </div>

            {/* Expenses by Category */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Dépenses par Catégorie
              </h3>
              <div className="space-y-3">
                {Object.entries(summary.expenses_by_category).length > 0 ? (
                  Object.entries(summary.expenses_by_category).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-gray-700">{category}</span>
                      <span className="font-semibold text-red-600">${amount.toLocaleString()}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucune dépense enregistrée</p>
                )}
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          {summary.monthly_trend && summary.monthly_trend.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Tendance Mensuelle
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mois</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenus</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dépenses</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {summary.monthly_trend.map((month) => (
                      <tr key={month.month}>
                        <td className="px-4 py-3 text-sm text-gray-900">{month.month}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                          ${month.revenue.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                          ${month.expenses.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${
                          month.profit >= 0 ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          ${month.profit.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Budget Summary */}
          {summary.budget_summary && summary.budget_summary.categories > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget du Mois en Cours</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Planifié</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ${summary.budget_summary.total_planned.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Dépensé</div>
                  <div className="text-2xl font-bold text-red-600">
                    ${summary.budget_summary.total_spent.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Restant</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${summary.budget_summary.total_remaining.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Catégories</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {summary.budget_summary.categories}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transactions Récentes</h3>
            {transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((trans) => (
                      <tr key={trans.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">{trans.date}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            trans.type === 'revenue'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {trans.type === 'revenue' ? 'Revenu' : 'Dépense'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{trans.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{trans.description}</td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${
                          trans.type === 'revenue' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {trans.type === 'revenue' ? '+' : '-'}${trans.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <button
                            onClick={() => {
                              setSelectedTransaction(trans);
                              setShowTransactionForm(true);
                            }}
                            className="text-amber-600 hover:text-amber-800 mr-3"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => deleteTransaction(trans.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune transaction enregistrée</p>
            )}
          </div>
        </div>
      )}

      {/* Budgets Tab */}
      {activeTab === 'budgets' && (
        <div className="space-y-6">
          {budgets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {budgets.map((budget) => (
                <div key={budget.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">{budget.category}</h4>
                    <span className="text-xs text-gray-500">
                      {budget.month}/{budget.year}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Planifié</span>
                      <span className="font-medium text-gray-900">${budget.planned_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Dépensé</span>
                      <span className="font-medium text-red-600">${budget.spent_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Restant</span>
                      <span className="font-medium text-green-600">${budget.remaining_amount.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                      <span>Utilisation</span>
                      <span>{budget.usage_percentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          budget.usage_percentage > 90
                            ? 'bg-red-500'
                            : budget.usage_percentage > 75
                            ? 'bg-orange-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(budget.usage_percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedBudget(budget);
                      setShowBudgetForm(true);
                    }}
                    className="mt-4 w-full text-center text-sm text-amber-600 hover:text-amber-800 font-medium"
                  >
                    Modifier
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Aucun budget créé pour ce mois</p>
              <button
                onClick={() => setShowBudgetForm(true)}
                className="text-amber-600 hover:text-amber-800 font-medium"
              >
                Créer votre premier budget
              </button>
            </div>
          )}
        </div>
      )}

      {/* Transaction Form Modal */}
      {showTransactionForm && (
        <TransactionForm
          transaction={selectedTransaction}
          apiKey={apiKey}
          onClose={() => {
            setShowTransactionForm(false);
            setSelectedTransaction(null);
          }}
          onSave={handleTransactionSave}
        />
      )}

      {/* Budget Form Modal */}
      {showBudgetForm && (
        <BudgetForm
          budget={selectedBudget}
          apiKey={apiKey}
          onClose={() => {
            setShowBudgetForm(false);
            setSelectedBudget(null);
          }}
          onSave={handleBudgetSave}
        />
      )}
    </div>
  );
};

export default Finances;
